import type { ForgePluginId } from "./pluginsPage.logic";

export interface PluginSkillMetadata {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly icon: "brand" | "skill";
}

export interface PluginDetailMetadata {
  readonly id: ForgePluginId;
  readonly title: string;
  readonly tagline: string;
  readonly promptLabel: string;
  readonly prompt: string;
  readonly body: string;
  readonly appDescription: string;
  readonly skills: ReadonlyArray<PluginSkillMetadata>;
  readonly capabilities: string;
  readonly access: string;
  readonly auth: string;
  readonly managedBy: string;
  readonly provider: string;
  readonly category: string;
  readonly providerUrl: string;
}

const PLUGIN_DETAIL_BY_ID: Readonly<Record<ForgePluginId, PluginDetailMetadata>> = {
  gmail: {
    id: "gmail",
    title: "Gmail",
    tagline: "Read and reference Gmail",
    promptLabel: "Gmail",
    prompt: "Summarize inbox activity, draft replies, or process email threads",
    body: "Use Gmail to summarize inbox activity, draft replies, and organize email threads through the connected Gmail app.",
    appDescription: "Find and reference emails from your inbox.",
    skills: [
      {
        id: "gmail",
        name: "Gmail",
        description: "Summarize threads and draft replies.",
        icon: "skill",
      },
      {
        id: "inbox-triage",
        name: "Inbox Triage",
        description: "Triage an inbox into action buckets.",
        icon: "skill",
      },
    ],
    capabilities: "Search, summarize, and cite email context",
    access: "Read-only Gmail context",
    auth: "Google OAuth through CMMD",
    managedBy: "Forge plugins",
    provider: "Google Gmail",
    category: "Communication",
    providerUrl: "https://mail.google.com/",
  },
  "google-calendar": {
    id: "google-calendar",
    title: "Google Calendar",
    tagline: "Read and reference calendar events",
    promptLabel: "Calendar",
    prompt: "Find meetings, summarize upcoming events, or prepare daily schedules",
    body: "Use Google Calendar to understand availability, review upcoming meetings, and bring schedule context into Forge conversations.",
    appDescription: "Find and reference events from your calendars.",
    skills: [
      {
        id: "calendar",
        name: "Calendar",
        description: "Summarize events and upcoming meetings.",
        icon: "skill",
      },
      {
        id: "meeting-prep",
        name: "Meeting Prep",
        description: "Prepare for meetings from calendar context.",
        icon: "skill",
      },
    ],
    capabilities: "Find events, summarize meetings, and cite schedule context",
    access: "Read-only calendar context",
    auth: "Google OAuth through CMMD",
    managedBy: "Forge plugins",
    provider: "Google Calendar",
    category: "Productivity",
    providerUrl: "https://calendar.google.com/",
  },
  "computer-use": {
    id: "computer-use",
    title: "Computer Use",
    tagline: "Let agents see and control this desktop",
    promptLabel: "Computer",
    prompt: "Take a screenshot of my screen, or click and type in another app",
    body: "Give agents a screenshot of this Mac and the ability to move the mouse, click, type, scroll, and drag. Off by default. Turning it on grants the agent the same control over this machine that you have, so enable it only for work you are watching. Screen contents are treated as untrusted data, never as instructions.",
    appDescription: "Screenshot and control this desktop.",
    skills: [
      {
        id: "computer-use",
        name: "Computer Use",
        description: "Screenshot the desktop and drive the mouse and keyboard.",
        icon: "skill",
      },
    ],
    capabilities: "Screenshot, click, type, scroll, and drag on this desktop",
    access: "Full control of this desktop while enabled",
    auth: "macOS Screen Recording and Accessibility permissions",
    managedBy: "Forge plugins",
    provider: "Forge Desktop",
    category: "Productivity",
    providerUrl:
      "https://support.apple.com/guide/mac-help/change-privacy-security-settings-mchl211c911f/mac",
  },
  github: {
    id: "github",
    title: "GitHub",
    tagline: "Build and ship with repositories",
    promptLabel: "GitHub",
    prompt: "Inspect pull requests, push branches, or follow workflow runs",
    body: "Use GitHub to create repositories, link project remotes, push branches, open pull requests, inspect checks, and manage workflow runs through Forge brokered tools.",
    appDescription: "Create repos, open PRs, and manage workflow runs.",
    skills: [
      {
        id: "github",
        name: "GitHub",
        description: "Inspect pull requests, issues, checks, and workflows.",
        icon: "brand",
      },
      {
        id: "publish-changes",
        name: "Publish Changes",
        description: "Push a branch and open a pull request.",
        icon: "brand",
      },
      {
        id: "github-fix-ci",
        name: "GitHub Fix CI",
        description: "Debug failing GitHub Actions runs.",
        icon: "brand",
      },
      {
        id: "github-address-comments",
        name: "GitHub Address Comments",
        description: "Address pull request review feedback.",
        icon: "brand",
      },
    ],
    capabilities: "Repository creation, branch pushes, pull requests, issues, and workflow runs",
    access: "Brokered GitHub access scoped by the connected Forge account",
    auth: "GitHub OAuth through Forge and CMMD",
    managedBy: "Forge GitHub integration",
    provider: "GitHub",
    category: "Developer Tools",
    providerUrl: "https://github.com/",
  },
};

export function allPluginDetailMetadata(): ReadonlyArray<PluginDetailMetadata> {
  return Object.values(PLUGIN_DETAIL_BY_ID);
}

export function pluginDetailMetadata(pluginId: ForgePluginId): PluginDetailMetadata {
  return PLUGIN_DETAIL_BY_ID[pluginId];
}
